import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faWrench, faPaintBrush, faArrowLeft, faChevronDown, faRightFromBracket, faRotate, faBolt, faBug, faCloudUpload, faCode, faTimeline, faToolbox } from "@fortawesome/free-solid-svg-icons"
import styles from "./ConfigurationTopBar.module.css"
import clsx from "clsx"
import React, { useState } from "react"
import { createPortal } from "react-dom"
import "@fontsource-variable/exo-2"
import { logoutAppLegacy, logoutPlatformLegacy, useWistorAuth } from "../api/auth"
import { Trigger, TriggerPopup } from './TriggerPopup'
import { useSelector } from "../store"
import { Tooltip } from '@mui/material'
import { AppVariablesSidebar } from './AppVariablesSideBar'
import { EventLogsPopup } from "./EventLogs/EventLogsPopup"
import { isEditMode } from "../utils/is-edit-mode"
import { DndContext } from "@dnd-kit/core"
import { posthog } from 'posthog-js'
import { RuleExecutionLogsPopup } from "./RuleExecutionLogs/RuleLogsPopup"
import { useRuleTrackingIsEnabled } from "./RuleExecutionLogs/rule-execution-logs-store"
import { featureFlags, useFeatureFlags } from '../flags'
import { useAppLoginStore } from '../api/app-login'
import { SparqlPopup } from './SparqlPopup'
import { ToolName } from './StandaloneToolPage'
import { getToolSyncChannel, ToolSyncMessage } from '../helpers/tool-sync'
import { Sparql } from '../icons/sparql'
import { SvgIcon } from './svg-icon'

interface PubSubVariable {
  name: string
  value: any
}

type VisibleElement = {eventLogs: boolean, triggers: boolean, appVariables: boolean, ruleExecutionLogs: boolean, sparql: boolean}

const DEFAULT_VISIBLE_ELEMENT = {eventLogs: false, triggers: false, appVariables: false, ruleExecutionLogs: false, sparql: false} as const satisfies VisibleElement

export function PlatformConfigationTopBar(props: {
  projectPath: string
}) {
  const auth = useWistorAuth()
  return (
    <TopBar>
        <Item to={`/projects/${props.projectPath}`}>
          <FontAwesomeIcon
            icon={faArrowLeft}
            className="back-button"
          />
        </Item>
        {auth.status === "authorized" &&
          auth.isSingleLogin
            ? (
              <Item onClick={async () => {
                if (!auth.isAuthenticated) throw new Error('A malfunction was detected. It should not be possible to click this button.')
                posthog.reset()
                auth.logout()
              }}>
                <FontAwesomeIcon icon={faRotate} />
                <IconText>Log out</IconText>
              </Item>
            )
            : (<>
              <Item onClick={() => {
                logoutPlatformLegacy()
                window.location.href = "/"
              }}>
                <FontAwesomeIcon icon={faRightFromBracket} />
                <IconText>Log out platform</IconText>
              </Item>
            </>)
        }
    </TopBar>
  )
}

export function AppConfigurationTopBar(props: {
  isPlatform?: boolean
  projectPath: string
  viewerPath: string
  repoURL: string
  repoName: string
  localRepoURL?: string
  openAppSettings: () => void
  mode: "edit" | "preview"
  saveTriggers: (triggers: Trigger[]) => Promise<void>
  triggers: Trigger[]
}) {
  const [visible, setVisible] = useState<VisibleElement>(DEFAULT_VISIBLE_ELEMENT)
  const appVars = useSelector(s => s.pubsub ?? {})
  const ruleTrackingIsEnabled = useRuleTrackingIsEnabled()

  const auth = useWistorAuth()
  const goToLoginPage = useAppLoginStore.getState().goToLoginPage

  // Respond to tool-sync requests from child windows
  React.useEffect(() => {
    const ch = getToolSyncChannel()
    const handler = (e: MessageEvent<ToolSyncMessage>) => {
      if (e.data.type === 'request-repo') {
        ch.postMessage({
          type: 'repo-response',
          repoURL: props.repoURL,
        } satisfies ToolSyncMessage)
      }
    }
    ch.addEventListener('message', handler)
    return () => ch.removeEventListener('message', handler)
  }, [props.repoURL])

  const localModeIsActive = useFeatureFlags().localMode
  if (!localModeIsActive && (auth.status !== 'authorized' || auth.user.role === 'viewer')) {
    return null
  }

  return (
    <>

      {visible.triggers && createPortal(
        <TriggerPopup
          open
          onClose={() => setVisible(v => ({...v, triggers: false}))}
          triggers={props.triggers}
          saveTriggers={props.saveTriggers}
        />,
        document.body)}

      {visible.eventLogs && createPortal(
        <DndContext>
          <EventLogsPopup open onClose={() => setVisible(v => ({...v, eventLogs: false}))} />
        </DndContext>,
        document.body)}

      {visible.appVariables && createPortal(
        <AppVariablesSidebar
          isOpen={visible.appVariables}
          onClose={() => setVisible(v => ({...v, appVariables: false}))}
          variables={appVars}
        />,
        document.body
      )}

      {visible.ruleExecutionLogs && createPortal(
        <RuleExecutionLogsPopup open onClose={() => setVisible(v => ({...v, ruleExecutionLogs: false}))} />,
      document.body)}

      {visible.sparql && createPortal(
        <SparqlPopup
          onClose={() => setVisible(v => ({...v, sparql: false}))}
          repoURL={props.repoURL}
          repoName={props.repoName}
          localRepoURL={props.localRepoURL}
        />,
        document.body
      )}

      <TopBar small>
        <Item to={`/projects/${props.projectPath}`}>
          <FontAwesomeIcon
            icon={faArrowLeft}
            className="back-button"
          />
        </Item>
        <Item
          active={props.mode === 'edit'}
          to={toggleEditModeUrl(props.mode, props.projectPath, props.viewerPath)}>
          <FontAwesomeIcon icon={faPaintBrush} />
          <IconText>Edit mode</IconText>
        </Item>
        <Item onClick={props.openAppSettings}>
          <FontAwesomeIcon icon={faWrench} className={"iets"} />
          <IconText>App settings</IconText>
        </Item>
        <Item onClick={() => setVisible(v => ({...v, triggers: !v.triggers}))}>
          <FontAwesomeIcon icon={faBolt} />
          <IconText>Triggers</IconText>
        </Item>
        <Item
          active={visible.appVariables}
          onClick={() => setVisible(v => ({...v, appVariables: !v.appVariables}))}
        >
          <FontAwesomeIcon icon={faCode} />
          <IconText>App Variables</IconText>
        </Item>
        <Tooltip title={
          <span>
            Shows logs of all wistor-events. <br />
            This only works in edit-mode as it may impact User Experience.
          </span>
        }>
          <Item active={visible.eventLogs} disabled={!isEditMode()}
            onClick={() => setVisible(v => ({...v, eventLogs: true}))}
          >
            <FontAwesomeIcon icon={faTimeline} />
            <IconText>Event logs</IconText>
          </Item>
        </Tooltip>
        <Tooltip title={
          <span>
            Enables rule engine logging in developer console. <br />
            This may impact performance and increase server resource usage.
          </span>
        }>
          <Item active={visible.ruleExecutionLogs || ruleTrackingIsEnabled}
            onClick={() => setVisible(v => ({...v, ruleExecutionLogs: !v.ruleExecutionLogs}))}
          >
            <FontAwesomeIcon icon={faBug} />
            <IconText>Debug rules</IconText>
          </Item>
        </Tooltip>
        <Tooltip title={
          <span>
            Lock&Publish the app to an existing URL. Not automated yet.
          </span>
        }>
          <Item
            onClick={() => {window.open("https://bim-connected.slite.com/app/docs/-1nAHhv2p9KvWC/Lock-and-Publish-guidelines")}}>
            <FontAwesomeIcon icon={faCloudUpload} />
            <IconText>Lock&Publish</IconText>
          </Item>
        </Tooltip>
        <Tooltip title={
          <span>
            A helper tool to run SPARQL queries
          </span>
        }>
          <Item
            active={visible.sparql}
            onClick={() => setVisible(v => ({...v, sparql: !v.sparql}))}
          >
            <SvgIcon icon={Sparql} size={14} />
            <IconText>SPARQL</IconText>
          </Item>
        </Tooltip>
          <Item chevron>
            <FontAwesomeIcon icon={faToolbox} />
            <IconText>Platform tools</IconText>
            <SubMenu>
              <Item onClick={() => openToolWindow('rule-editor', props.repoURL)}>
                <IconText>Rule Editor</IconText>
              </Item>
              <Item onClick={() => openToolWindow('ontology-editor', props.repoURL)}>
                <IconText>Ontology Editor</IconText>
              </Item>
              <Item onClick={() => openToolWindow('ontology-viewer', props.repoURL)}>
                <IconText>Ontology Viewer</IconText>
              </Item>
            </SubMenu>
          </Item>

        <Separator />

        {auth.status === "authorized" && (
          <Item chevron>
            <span>{auth.user.name}</span>
            <FontAwesomeIcon icon={faUser} style={{ marginBottom: '2px' }} />
            <SubMenu>
              {auth.isSingleLogin
                ? (
                  <Item onClick={async () => {
                    if (!auth.isAuthenticated) throw new Error('somehow ')
                    posthog.reset()
                    await logoutAppLegacy(props.repoURL)
                    auth.logout()
                  }}>
                    <FontAwesomeIcon icon={faRotate} />
                    <IconText>Log out</IconText>
                  </Item>
                )
                : (<>
                  <Item onClick={async () => {
                    await logoutAppLegacy(props.repoURL)
                    window.location.reload()
                  }}>
                    <FontAwesomeIcon icon={faRotate} />
                    <IconText>Log out / reset app session</IconText>
                  </Item>
                  <Item onClick={goToLoginPage} disabled={!goToLoginPage}>
                    <FontAwesomeIcon icon={faRotate} />
                    <IconText>Log in as different user</IconText>
                  </Item>
                  <Item onClick={() => {
                    logoutPlatformLegacy()
                    window.location.href = "/"
                  }}>
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    <IconText>Log out platform</IconText>
                  </Item>
                </>)
              }
            </SubMenu>
          </Item>
        )}
      </TopBar>
    </>
  )
}

function TopBar(props: {
  children: React.ReactNode,
  small?: boolean
}) {
  return <ul className={clsx(styles["top-bar"], "flex items-center", {
    [styles["small"]]: !!props.small
  })}>{props.children}</ul>
}

function SubMenu(props: {
  children: React.ReactNode
}) {
  return <ul className={styles["sub-menu"]}>{props.children}</ul>
}

function Separator() {
  return <li className={styles["separator"]} />
}

function IconText(props: {
  children: React.ReactNode
}) {
  return <span className={styles["icon-text"]}>{props.children}</span>
}

const Item = React.forwardRef<HTMLLIElement, {
  to?: string | null
  onClick?: () => void
  children: React.ReactNode
  active?: boolean
  chevron?: boolean
  disabled?: boolean
}>((props, ref) => {
  const { to, onClick, children, active, chevron, ...otherProps } = props
  const isClickable = !props.disabled || !!props.to || !!props.onClick
  return (
    <li ref={ref} {...otherProps} className={clsx(styles["item"], {
      [styles["clickable"]]: !props.disabled && isClickable,
      [styles["active"]]: !!props.active,
      "opacity-75": props.disabled,
    })}>
      <Link to={props.to ?? "#"} onClick={e => {
        if (props.disabled) return
        if (!props.onClick) return
        e.preventDefault()
        props.onClick()
      }}>
        {props.children}
        {props.chevron && (
          <FontAwesomeIcon className={styles.chevron} icon={faChevronDown} />
        )}
      </Link>
    </li>
  )
})

function toggleEditModeUrl(
  mode: "edit" | "preview",
  projectPath?: string,
  viewerPath?: string,
): string {
  if (featureFlags.values.localMode) return `/${mode === "edit" ? "" : "edit"}${window.location.search}`
  return `/projects/${projectPath}/${viewerPath}/${mode === "edit" ? "" : "edit"}${window.location.search}`
}


function openToolWindow(toolName: ToolName, repoURL: string, localRepoURL?: string) {
  const params = new URLSearchParams({ repo: repoURL })
  if (localRepoURL) params.set('localRepo', localRepoURL)
  window.open(`/tools/${toolName}?${params}`, `tool-${toolName}`, 'width=1200,height=800')
}
